// src/pages/import/declaracoes-aduaneiras/estorno/DeclaracaoAduaneiraEstornoConsultPage.jsx
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';
import SimpleConsultPageTemplate from '../../../../templates/simple-consult-page/SimpleConsultPageTemplate/SimpleConsultPageTemplate';
import { useTranslation } from 'react-i18next';
import {
  fetchEstornoDAMercadoriasByFilterAsync,
  estornarItensDaAsync,
  estornarQuantidadeDaAsync,
} from '../../../../features/declaracoesAduaneiras/declaracoesAduaneirasThunks';
import {
  selectEstornoDAMercadoriasList,
  selectEstornoDAMercadoriasCount,
  selectEstornoDAMercadoriasStatus,
} from '../../../../features/declaracoesAduaneiras/declaracoesAduaneirasSlice';
import { isLoadingStatus } from '../../../../utils/store/store-utils';
import { setErrorFeedback, setSuccessFeedback } from '../../../../features/feedback/feedbackSlice';
import ReplayIcon from '@mui/icons-material/Replay';
import { Dialog, DialogContent, DialogTitle, DialogActions, Button, Tooltip, IconButton } from '@material-ui/core';
import QCXTextField from '../../../../shared-components/text-field/QCXTextField';
import { focusLockActions } from '../../../../features/focus-lock/focusLockSlice';

export default function DeclaracaoAduaneiraEstornoConsultPage({ authInfo = {} }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { id: followupDestinoId } = useParams();

  const list = useSelector(selectEstornoDAMercadoriasList);
  const count = useSelector(selectEstornoDAMercadoriasCount);
  const status = useSelector(selectEstornoDAMercadoriasStatus);
  const isLoading = useMemo(() => isLoadingStatus(status), [status]);

  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);

  const [lastPage, setLastPage] = useState(1);
  const [lastSize, setLastSize] = useState(10);
  const [lastFilter, setLastFilter] = useState('');

  const [dlgQtdOpen, setDlgQtdOpen] = useState(false);
  const [mercadoriaIdEstorno, setMercadoriaIdEstorno] = useState(null);
  const [qtdEstorno, setQtdEstorno] = useState('');
  const [maxQtdEstorno, setMaxQtdEstorno] = useState(null);

  const handleFocusInput = useCallback(() => {
    dispatch(focusLockActions.unlock());
  }, [dispatch]);

  const handleBlurInput = useCallback(() => {
    dispatch(focusLockActions.lock());
  }, [dispatch]);

  const openDlgQtd = (row) => {
    setMercadoriaIdEstorno(row?.id);
    setMaxQtdEstorno(row?.qtdeBaixada ?? 0);
    setQtdEstorno(row?.qtdeBaixada ? String(row.qtdeBaixada) : '');
    setDlgQtdOpen(true);
  };

  const fmt = (v) => new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 6 }).format(Number(v || 0));

  const columns = useMemo(
    () => [
      {
        field: 'processo',
        headerName: 'Processo',
        headerAlign: 'center',
        align: 'center',
        flex: 140,
        valueGetter: ({ row }) => row?.processo || '-',
      },
      {
        field: 'pn',
        headerName: 'Pn',
        flex: 160,
        valueGetter: ({ row }) => row?.partNumber || '-',
      },
      {
        field: 'descricao',
        headerName: 'Descrição',
        flex: 320,
        valueGetter: ({ row }) => row?.descricao || '-',
      },
      {
        field: 'qtdeBaixada',
        headerName: 'Qtde Baixada',
        align: 'right',
        width: 160,
        valueGetter: ({ row }) => fmt(row?.qtdeBaixada),
      },
      {
        field: 'actions',
        headerName: 'Ações',
        headerAlign: 'center',
        align: 'center',
        width: 110,
        sortable: false,
        renderCell: ({ row }) => (
          <Tooltip title="Estornar com quantidade">
            <IconButton onClick={() => openDlgQtd(row)}>
              <ReplayIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        ),
      },
    ],
    [fmt]
  );

  const fetchGrid = useCallback(
    ({ currentPage, pageSize, filterInputValue }) => {
      setLastPage(currentPage);
      setLastSize(pageSize);
      setLastFilter(filterInputValue || '');
      dispatch(
        fetchEstornoDAMercadoriasByFilterAsync([
          { name: 'page', value: currentPage },
          { name: 'size', value: pageSize },
          { name: 'followupId', value: followupDestinoId },
          { name: 'search', value: filterInputValue || '' },
        ])
      );
    },
    [dispatch, followupDestinoId]
  );

  useEffect(() => {
    fetchGrid({ currentPage: 1, pageSize: 10, filterInputValue: '' });
  }, [fetchGrid]);

  const handleConfigChange = useCallback(
    ({ currentPage, pageSize, filterInputValue }) => {
      fetchGrid({ currentPage, pageSize, filterInputValue });
    },
    [fetchGrid]
  );

  const handleConfirmarEstornoQtd = async () => {
    if (!mercadoriaIdEstorno) {
      dispatch(setErrorFeedback({ message: 'Mercadoria inválida.' }));
      return;
    }
    const qtdNumber = Number(qtdEstorno.replace(',', '.'));
    if (!qtdEstorno || qtdNumber <= 0) {
      dispatch(setErrorFeedback({ message: 'Informe uma quantidade maior que zero.' }));
      return;
    }
    if (maxQtdEstorno != null && qtdNumber > Number(maxQtdEstorno)) {
      dispatch(setErrorFeedback({ message: 'Quantidade não pode ser maior que a baixada.' }));
      return;
    }

    const res = await dispatch(
      estornarQuantidadeDaAsync({
        followupDestinoId: Number(followupDestinoId),
        mercadoriaCloneId: Number(mercadoriaIdEstorno),
        quantidade: qtdNumber,
      })
    );

    if (estornarQuantidadeDaAsync.fulfilled.match(res)) {
      setDlgQtdOpen(false);
      fetchGrid({ currentPage: 1, pageSize: lastSize, filterInputValue: lastFilter });
    }
  };

  const controlButtons = useMemo(
    () =>
      selectMode
        ? [
          {
            description: 'ESTORNAR',
            color: 'secondary',
            onClick: async () => {
              if (!selectedIds?.length) {
                dispatch(setErrorFeedback({ message: 'Selecione ao menos uma mercadoria.' }));
                return;
              }
              const res = await dispatch(
                estornarItensDaAsync({
                  followupDestinoId: Number(followupDestinoId),
                  mercadoriaIds: selectedIds.map(Number),
                })
              );
              if (estornarItensDaAsync.fulfilled.match(res)) {
                dispatch(setSuccessFeedback({ message: 'Estorno realizado com sucesso.' }));
                setSelectedIds([]);
                setSelectMode(false);
                fetchGrid({ currentPage: 1, pageSize: lastSize, filterInputValue: lastFilter });
              }
            },
            disabled: !selectedIds?.length,
          },
          {
            description: 'Cancelar',
            variant: 'outlined',
            onClick: () => {
              setSelectMode(false);
              setSelectedIds([]);
            },
          },
        ]
        : [
          {
            description: 'SELECIONAR PARA ESTORNO',
            color: 'primary',
            onClick: () => setSelectMode(true),
          },
        ],
    [selectMode, selectedIds, dispatch, followupDestinoId, fetchGrid, lastSize, lastFilter]
  );

  const templateProps = useMemo(
    () => ({
      page: {
        title: 'Estorno de DA',
        icon: null,
        breadcrumbs: [
          { link: { to: '/', name: t('com.muralis.qcx.inicio') } },
          { link: { to: t('com.muralis.qcx.url.moduloImportacao'), name: t('com.muralis.qcx.importacao.label') } },
          { link: { to: '/importacao/declaracoes-aduaneiras', name: 'Declarações Adicionais' } },
          { text: { name: 'Estorno de DA' }, default: true },
        ],
      },
      table: {
        columns,
        rowsCount: count,
        onConfigChangeCallback: handleConfigChange,
        checkboxSelection: selectMode,
        onSelectionModelChange: (selectionModel) => {
          setSelectedIds(Array.isArray(selectionModel) ? selectionModel : selectionModel?.selectionModel || []);
        },
      },
    }),
    [columns, count, handleConfigChange, selectMode, t]
  );

  return (
    <>
      <SimpleConsultPageTemplate
        pageProps={templateProps.page}
        tableProps={templateProps.table}
        tableData={list}
        controlProps={{ others: controlButtons }}
        isLoading={isLoading}
        authInfo={authInfo}
        requiredRoles={['importacao-sda']}
      />

      <Dialog
        open={dlgQtdOpen}
        onClose={() => setDlgQtdOpen(false)}
        maxWidth="xs"
        fullWidth
        keepMounted
        disableEnforceFocus
        disableRestoreFocus
      >
        <DialogTitle>Estornar quantidade</DialogTitle>
        <DialogContent>
          <QCXTextField
            label="Quantidade a estornar"
            variant="outlined"
            fullWidth
            autoFocus
            value={qtdEstorno}
            onFocus={handleFocusInput}
            onBlur={handleBlurInput}
            onChange={(e) => {
              const raw = e.target.value.replace(/[^0-9.,]/g, '');
              const num = Number(raw.replace(',', '.'));

              if (maxQtdEstorno != null && num > Number(maxQtdEstorno)) {
                return;
              }

              setQtdEstorno(raw);
            }}
            onKeyDown={(e) => {
              e.stopPropagation();
              const blocked = ['e', 'E', '+', '-'];
              if (blocked.includes(e.key)) e.preventDefault();
            }}
            inputProps={{ inputMode: 'decimal', pattern: '[0-9]*[.,]?[0-9]*' }}
            error={
              !!qtdEstorno &&
              maxQtdEstorno != null &&
              Number(qtdEstorno.replace(',', '.')) > Number(maxQtdEstorno)
            }
            helperText={maxQtdEstorno ? `Máximo: ${maxQtdEstorno}` : ' '}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDlgQtdOpen(false)}>Cancelar</Button>
          <Button
            color="secondary"
            variant="contained"
            onClick={handleConfirmarEstornoQtd}
            disabled={!qtdEstorno || Number(qtdEstorno.replace(',', '.')) <= 0}
          >
            Estornar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
